import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CloudUpload as UploadIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';
import {
  getBulkUploadJobs,
  openKnowledgeGeneratorDraft,
  type BulkUploadJob,
  type KnowledgeGeneratorTask,
} from '../../services/apiService';
import { isSuperadminInitialDraft } from '../../utils/knowledgeTaskOrigin';
import BulkUploadModal from './BulkUploadModal';
import { KnowledgeSectionHeading, KnowledgeStatus } from './KnowledgePrimitives';

interface ImportsAndDraftsProps {
  kgTasks: KnowledgeGeneratorTask[];
  activeDraftTask: KnowledgeGeneratorTask | null;
  onOpenDraft: (taskId?: string) => void;
  onRetryWebsiteTask: (taskId: string) => Promise<void>;
  // Starts a fresh website-sourced draft through the reviewed/gated pipeline.
  // The old ungated /knowledge-generator page has been retired. Undefined when there is no
  // prior task to source a company name/URL from (a company invited before
  // KB seeding existed); the CTAs below hide themselves in that case rather
  // than link to the retired page.
  onScanWebsite?: () => void;
  websiteScanPending?: boolean;
  websiteScanError?: string | null;
  /** A spreadsheet import lands server-side asynchronously — called once its
   *  job settles so the caller can refetch task/readiness data and reflect
   *  the newly added rows (e.g. `useKbReadiness`'s `refresh`). */
  onImported?: () => void;
}

const taskStatusLabel = (status: KnowledgeGeneratorTask['status']) => {
  switch (status) {
    case 'ready_for_review': return 'Ready to review';
    case 'pending':
    case 'researching':
    case 'generating': return 'Preparing';
    case 'syncing': return 'Publishing';
    case 'completed': return 'Published';
    case 'error': return 'Failed';
    default: return status;
  }
};

const sourceLabel = (task: Pick<KnowledgeGeneratorTask, 'createdBy' | 'generationMode'>) => {
  if (task.generationMode === 'rescan') return 'Website re-scan';
  if (isSuperadminInitialDraft(task.createdBy)) return 'Superadmin initial draft';
  if (task.createdBy === 'onboarding-wizard') return 'Guided setup';
  if (task.createdBy === 'spreadsheet-upload') return 'Spreadsheet';
  return 'Website scan';
};

const ImportsAndDrafts: React.FC<ImportsAndDraftsProps> = ({
  kgTasks,
  activeDraftTask,
  onOpenDraft,
  onRetryWebsiteTask,
  onScanWebsite,
  websiteScanPending = false,
  websiteScanError,
  onImported,
}) => {
  const [bulkJobs, setBulkJobs] = useState<BulkUploadJob[]>([]);
  const [retrying, setRetrying] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadDraftTaskId, setUploadDraftTaskId] = useState<string | null>(null);
  const [openingUploadDraft, setOpeningUploadDraft] = useState(false);
  const [uploadDraftError, setUploadDraftError] = useState<string | null>(null);

  const fetchBulkJobs = useCallback(() => {
    let active = true;
    getBulkUploadJobs(1, 10)
      .then((response) => {
        if (active) setBulkJobs(response.jobs || []);
      })
      .catch(() => {
        if (active) setBulkJobs([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => fetchBulkJobs(), [fetchBulkJobs]);

  const failedTask = kgTasks.find((task) => task.status === 'error') || null;
  const activeTaskIsReviewable = Boolean(
    activeDraftTask && (
      activeDraftTask.status === 'ready_for_review' ||
      (activeDraftTask.status === 'completed' && (activeDraftTask.unpublishedDraftCount || 0) > 0)
    )
  );
  const isPreparing = !!activeDraftTask && !activeTaskIsReviewable;
  const suggestions = activeDraftTask?.suggestions;
  // The list endpoint this task came from omits the full suggestions arrays
  // to stay light — it returns aggregated counts instead, so prefer those
  // and only fall back to the (normally empty) arrays if counts are absent.
  const answerCount =
    (activeDraftTask?.suggestedObjectionsCount ?? suggestions?.objections?.length ?? 0) +
    (activeDraftTask?.suggestedFaqsCount ??
      (suggestions?.websiteFaqs?.length ?? 0) + (suggestions?.aiGeneratedFaqs?.length ?? 0));
  const draftSourceLabels = useMemo(() => {
    if (!activeDraftTask) return [];
    const labels = new Set<string>([sourceLabel(activeDraftTask)]);
    bulkJobs
      .filter((job) => String(job.draftTaskId) === activeDraftTask._id && job.status !== 'failed')
      .forEach(() => labels.add('Spreadsheet'));
    if (activeDraftTask.createdBy !== 'onboarding-wizard' && (
      activeDraftTask.onboardingDraft?.landmines?.length ||
      activeDraftTask.onboardingDraft?.competitors?.length
    )) labels.add('Guided setup');
    return Array.from(labels);
  }, [activeDraftTask, bulkJobs]);

  const uploadSpreadsheet = async () => {
    if (openingUploadDraft) return;
    setUploadDraftError(null);
    setOpeningUploadDraft(true);
    try {
      const draft = activeDraftTask || await openKnowledgeGeneratorDraft();
      setUploadDraftTaskId(draft._id);
      setShowBulkUploadModal(true);
    } catch (error) {
      setUploadDraftError(error instanceof Error ? error.message : 'Could not open a knowledge draft.');
    } finally {
      setOpeningUploadDraft(false);
    }
  };

  const retryWebsite = async () => {
    if (!failedTask || retrying) return;
    setRetrying(true);
    try {
      await onRetryWebsiteTask(failedTask._id);
    } finally {
      setRetrying(false);
    }
  };

  // A ready-for-review draft already gets its own "Review draft" CTA from
  // the Overview page's "Needs your attention" list (useKbReadiness's
  // 'kg-drafts' item points at this exact task) — showing a second "Review
  // draft" button here duplicated that entry rather than adding information.
  // The preparing/no-draft states below have no such overlap and stay.
  const showCurrentDraftSection = !activeTaskIsReviewable;

  return (
    <>
    <div className="kb-imports space-y-8">
      {showCurrentDraftSection && (
      <section aria-labelledby="current-draft-heading">
        <KnowledgeSectionHeading
          id="current-draft-heading"
          title="Current knowledge draft"
          description="Every source adds to this one draft. Nothing goes live until you publish it."
        />

        {activeDraftTask ? (
          <div className="border border-hairline rounded-lg p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <KnowledgeStatus tone={activeDraftTask.status === 'ready_for_review' ? 'success' : 'info'}>
                  {taskStatusLabel(activeDraftTask.status)}
                </KnowledgeStatus>
                <span className="text-xs text-ink-muted">Sources: {draftSourceLabels.join(', ')}</span>
              </div>
              <p className="text-sm text-ink-secondary mt-3">
                {isPreparing
                  ? 'Apexia Labs is preparing the draft. You can leave this page and return later.'
                  : `${answerCount} answer${answerCount === 1 ? '' : 's'} drafted, plus company context and any guided additions.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenDraft(activeDraftTask._id)}
              className="kb-button kb-button-primary flex-shrink-0 px-4 py-2 text-sm"
            >
              {activeDraftTask.status === 'ready_for_review' ? 'Review draft' : 'View progress'}
            </button>
          </div>
        ) : (
          <div className="border border-hairline rounded-lg p-5">
            <h3 className="text-sm font-semibold text-ink">No unpublished changes</h3>
            <p className="text-sm text-ink-secondary mt-1">Your published knowledge remains available. Add another source when you have something new.</p>
            {failedTask && (
              <button type="button" onClick={retryWebsite} disabled={retrying} className="kb-button kb-button-secondary mt-3 px-3 py-2 text-sm">
                {retrying ? 'Retrying…' : 'Retry website scan'}
              </button>
            )}
          </div>
        )}
      </section>
      )}

      <section aria-labelledby="add-knowledge-heading" data-tutorial="knowledge-sources">
        <KnowledgeSectionHeading id="add-knowledge-heading" title="Add more knowledge" />
        <div className="kb-source-grid">
          <div className="kb-source-option flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <UploadIcon className="text-ink-muted mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink">Spreadsheet</p>
                <p className="text-xs text-ink-secondary mt-0.5">Add missing objections and FAQs without replacing current content.</p>
              </div>
            </div>
            <button type="button" onClick={uploadSpreadsheet} disabled={isPreparing || openingUploadDraft} data-tutorial="bulk-upload" className="kb-button kb-button-secondary px-3 py-2 text-sm font-medium disabled:opacity-50">
              {openingUploadDraft ? 'Opening draft…' : 'Upload'}
            </button>
          </div>

          {!failedTask && onScanWebsite && !isPreparing && (
            <div className="kb-source-option flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <WebsiteIcon className="text-ink-secondary mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-ink">Website scan</p>
                </div>
              </div>
              <button type="button" onClick={onScanWebsite} disabled={websiteScanPending} className="kb-button kb-button-secondary px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                {websiteScanPending ? 'Starting scan…' : 'Scan website'}
              </button>
            </div>
          )}
          {websiteScanError && (
            <p role="alert" className="py-3 text-xs text-error">{websiteScanError}</p>
          )}
          {uploadDraftError && (
            <p role="alert" className="py-3 text-xs text-error">{uploadDraftError}</p>
          )}
        </div>
      </section>

    </div>
    {showBulkUploadModal && uploadDraftTaskId && (
      <BulkUploadModal
        draftTaskId={uploadDraftTaskId}
        onClose={() => setShowBulkUploadModal(false)}
        onJobSettled={() => {
          fetchBulkJobs();
          onImported?.();
        }}
      />
    )}
    </>
  );
};

export default ImportsAndDrafts;
